import * as mobxReact from 'mobx-react';
import React, { useState } from 'react';
import cn from 'classnames';
import {
    ConnectionType,
    IChildConnectionDTO,
    IConnectionAttendeeDTO,
    IConnectionDetailsDTO,
    IConnectionGroupDTO,
    IParentConnectionDTO,
    onChangeConnectionEntity
} from '../../../../../api/connections.dao';
import Text from '../../../../global/text/text';
import { ConnectionDropdownPresentation, uniqueJoiningLink } from '../../shared/_connection-dropdown/connection-dropdown.presentation';
import { PresenterOrHostMeetingAttendee } from '../../../../../modules/connections/meeting/presenter-or-host-attendee-meeting.connection.entity';
import { ConnectionEntity } from '../../../../../modules/connections/abstract-connection.entity';
import { CopyButton } from '../../../../global/copy-button/copy-button';
import './attendee-connection.scss';
import { getKnovioPresentationUrl } from '../../../../../infrastructure/utils/knovio.utils';
import { getConnectionItemLabelText, getFilteredUniqueJoiningLink } from '../../../../../infrastructure/utils/connections.utils';
import { ConnectionVersion } from '../../../../../constants/common.constants';
import { CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS } from '../../../../../tracking-service/tracking-values';
import { AttendeeUniqueConnectionIndicatorWrapper } from './attendee-unique-connection-indicator-wrapper/attendee-unique-connection-indicator-wrapper';
import { ChildConnectionsViewModel } from '../_meeting-connections-form/child-connections/child-connections.viewmodel';
import {
    IDropdownConnectionGroupDetails
} from './attendee-unique-connection-indicator-wrapper/attendee-unique-connection-group-indicator/attendee-unique-connection-group-indicator';
import { AttendeeUniqueConnectionIndicator } from './attendee-unique-connection-indicator-wrapper/attendee-unique-connection-indicator/attendee-unique-connection-indicator';


export type AttendeeConnectionProps = {
    entity: ConnectionEntity<IConnectionAttendeeDTO> | PresenterOrHostMeetingAttendee;
    removeAttendee?: (attendeeId: number) => void;
    meetingConnections: IConnectionDetailsDTO[];
    connectionGroups: IConnectionGroupDTO[];
    derivedConnection: null | IConnectionDetailsDTO;
    editConnection?: (connection: IConnectionDetailsDTO) => void;
    onChangeMeetingConnection: (
        entity: onChangeConnectionEntity,
        connectionId: number | null,
        changeRegistrantOrHost: boolean,
    ) => void;
    isRoleRegistrant?: boolean;
    knovioReplayConnection?: IConnectionDetailsDTO;
    disabledDropDownConnection?: boolean;
    connectionVersion: ConnectionVersion;
    shouldRenderUniqueSection?: boolean;
    meetingParentConnections: IParentConnectionDTO[];
    handleTrackChildCopyBtn?: (event: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, type: string) => void;
    childConnectionsVM: ChildConnectionsViewModel;
    getConnectionsListFromGroup: (assignedGroup: IConnectionGroupDTO, attendeeId: number) => IDropdownConnectionGroupDetails[];
};

export const AttendeeConnection: React.FC<AttendeeConnectionProps> = mobxReact.observer((props: AttendeeConnectionProps): JSX.Element => {
    const {
        entity,
        removeAttendee,
        meetingConnections,
        editConnection,
        onChangeMeetingConnection,
        derivedConnection,
        isRoleRegistrant,
        knovioReplayConnection,
        disabledDropDownConnection,
        connectionVersion,
        connectionGroups,
        shouldRenderUniqueSection,
        meetingParentConnections,
        handleTrackChildCopyBtn,
        childConnectionsVM,
        getConnectionsListFromGroup,
    } = props;
    const [isOpenConnectionsInGroup, setIsOpenConnectionsInGroup] = useState<boolean>(false);
    const { firstName, lastName } = entity.getProps();
    const assignedConnectionId = entity.hasAssignedConnection() ? entity.getAssignedConnection() : null;

    const filteredUniqueJoiningLinkConnections = getFilteredUniqueJoiningLink(
        entity,
        meetingConnections,
        assignedConnectionId,
        isRoleRegistrant
    );

    const onChangeConnection = (connectionId: number | null, connectionOption?: string) => {
        if (entity.hasPropsValue('meetingConnectionType')) {
            entity.removePropsValue('meetingConnectionType');
        }
        if (connectionId && connectionOption === uniqueJoiningLink) {
            entity.setPropsValue('meetingConnectionType', uniqueJoiningLink);
        }
        const changePresenterOrHost = entity instanceof PresenterOrHostMeetingAttendee;
        onChangeMeetingConnection(entity, connectionId, changePresenterOrHost);
        setIsOpenConnectionsInGroup(false);
    };

    const webinarParticipantConnection = entity.getProps()?.meetingConnectionType === ConnectionType.UniqueJoiningLink
        && filteredUniqueJoiningLinkConnections.find(el => el.meetingConnectionId === assignedConnectionId)
    ;
    let webinarParticipantLink = webinarParticipantConnection?.connectionLink;
    if (webinarParticipantConnection?.type === ConnectionType.Knovio) {
        webinarParticipantLink = getKnovioPresentationUrl() + webinarParticipantConnection.embedCodeId;
    }

    const assignedConnection = assignedConnectionId || derivedConnection?.meetingConnectionId;
    const assignedConnectionGroup = connectionGroups.find(c => c.meetingConnectionId === assignedConnection);

    const getAttendeeParentConnection = (): IParentConnectionDTO | undefined => {
        return meetingParentConnections.find(c => String(c.meetingConnectionId) === String(assignedConnection));
    };
    const parentConnection = getAttendeeParentConnection();

    const getAttendeeChildConnection = (): IChildConnectionDTO | undefined => {
        if (!parentConnection) return;

        const panelistConnection = parentConnection.children.find(c =>
            c.type === ConnectionType.PanelistLink && c.attendeeId === entity.getId()
        );

        if (panelistConnection) {
            return panelistConnection;
        }
        return parentConnection.children.find(c => c.attendeeId === entity.getId());
    };

    const withLeftMargin = !isRoleRegistrant;
    const connectionsListFromGroup = assignedConnectionGroup ? getConnectionsListFromGroup(assignedConnectionGroup, entity.getId()) : [];
    if (isOpenConnectionsInGroup && !connectionsListFromGroup.length) {
        setIsOpenConnectionsInGroup(false);
    }

    return (
        <>
        <div key={entity.getId()} className='attendee-connection organization__row'>
            <Text className={shouldRenderUniqueSection
                ? 'organization__attendee'
                : 'organization__attendee--no-link'} tag='div' message={`${firstName} ${lastName}`} withEllipsis />
            {
                removeAttendee &&
                <Text
                    tag='label'
                    onClick={() => removeAttendee(entity.getId())}
                    className='organization__remove-label'
                    id='oe.common.remove'
                    description='Button label'
                    message='Remove'
                />
            }
            {shouldRenderUniqueSection &&
                <AttendeeUniqueConnectionIndicatorWrapper
                    parentConnection={getAttendeeParentConnection()}
                    childConnection={getAttendeeChildConnection()}
                    handleTrackChildCopyBtn={handleTrackChildCopyBtn}
                    withLeftMargin={withLeftMargin}
                    assignedConnectionGroup={assignedConnectionGroup}
                    entity={entity}
                    meetingConnections={meetingConnections}
                    parentConnections={meetingParentConnections}
                    childConnectionsVM={childConnectionsVM}
                    setIsOpenConnectionsInGroup={setIsOpenConnectionsInGroup}
                    isOpenConnectionsInGroup={isOpenConnectionsInGroup}
                />
            }
            <ConnectionDropdownPresentation
                assignedConnectionId={assignedConnectionId}
                meetingConnections={filteredUniqueJoiningLinkConnections}
                derivedConnection={props.derivedConnection}
                isLabelVisibleCondition={Boolean(knovioReplayConnection || assignedConnectionId || derivedConnection)}
                onChange={onChangeConnection}
                onAttendeeLevelDropdown={true}
                editConnection={editConnection}
                knovioReplayConnection={knovioReplayConnection}
                disabledDropDownConnection={disabledDropDownConnection}
                connectionVersion={connectionVersion}
                selectedAttendee={entity.getProps()}
                connectionGroups={connectionGroups}
                cssClass={shouldRenderUniqueSection ? 'w-150' : ''}
            />
            {webinarParticipantLink && !knovioReplayConnection && <CopyButton copyValue={webinarParticipantLink} withoutIcon />}
        </div>
        { isOpenConnectionsInGroup &&
            <div className='flex justify-content-end attendee-connection connection-group-details'>
                <div className='width-60' >
                    {
                        connectionsListFromGroup.map(({
                            connection, attendeeParentConnection, attendeeChildConnection
                        }) => {
                            return (
                                <div
                                    className='attendee-connection organization__row m-left-7px justify-content-end'
                                    key={connection.meetingConnectionId}
                                >
                                    <div
                                        className={cn('m-right-2px', {
                                            'attendee-connection__icon-indicator-wrapper': !withLeftMargin,
                                        })}
                                    >
                                        <AttendeeUniqueConnectionIndicator
                                            parentConnection={attendeeParentConnection}
                                            childConnection={attendeeChildConnection}
                                            childConnectionsVM={childConnectionsVM}
                                            withLeftMargin={withLeftMargin}
                                            propsCustomLeft={-30}
                                        />
                                    </div>
                                    <Text
                                        message={getConnectionItemLabelText(connection)}
                                        withEllipsis
                                        className='group-connection-attendee-title'
                                    />
                                </div>
                            );
                        })
                    }
                </div>
            </div>
        }
        </>
    );
});
